import { DrawableObject, RenderConfiguration, RenderMode, RenderSettings } from '../renderer';
import { PropertyAnimation } from '../animation';
import { ObjectInteraction } from '../object';
import { VertexBuffer } from '../mesh';
import { Context } from '../context/Context';
import { vec3 } from 'gl-matrix';
import { Camera } from '../camera';

export class LineList implements DrawableObject {
  public renderConfiguration: RenderConfiguration = {
    order: 6000,
    culling: 'none',
    static: true,
    modes: [RenderMode.Overlay],
  };
  animatedProperties?: PropertyAnimation;
  interaction?: ObjectInteraction;

  public ctx: Context;

  private buffer: VertexBuffer;
  private vao: WebGLVertexArrayObject;

  constructor(ctx: Context) {
    this.ctx = ctx;
    this.buffer = new VertexBuffer(ctx.gl.createBuffer(), ctx.gl.LINES, 0, 0);
    this.vao = this.createVAO();
    this.setLines([]);
  }

  public setLines(coordList: (vec3 | number[])[]) {
    this.setMultiLines([coordList]);
  }

  public setMultiLines(coordLists: (vec3 | number[])[][]) {
    let gl = this.ctx.gl;
    let geometry: number[] = [];

    for (let coordList of coordLists) {
      let previousCoord = coordList.length > 0 ? coordList[0] : vec3.create();
      for (let coords of coordList) {
        geometry.push(previousCoord[0], previousCoord[1], previousCoord[2]);
        geometry.push(coords[0], coords[1], coords[2]);

        previousCoord = coords;
      }
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(geometry), gl.DYNAMIC_DRAW);

    this.buffer.itemSize = 3;
    this.buffer.itemCount = geometry.length / 3;
  }

  public render(ctx: Context, camera: Camera, settings: RenderSettings) {
    let vertexBuffer = this.buffer;

    if (vertexBuffer.itemCount == 0) {
      return;
    }

    const gl = ctx.gl;

    let shader = ctx.engine.shaderManager.getTestGeometryShader();

    ctx.setShader(shader);

    gl.bindVertexArray(this.vao);

    shader.uniformsContainer
      .setModelViewMatrix(camera.modelViewMatrix)
      .setProjectionMatrix(camera.projectionMatrix);

    gl.drawArrays(vertexBuffer.glDrawMode, 0, vertexBuffer.itemCount);
  }

  private createVAO(): WebGLVertexArrayObject {
    const gl = this.ctx.gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    return vao;
  }
}
